import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, Modal, H4, S, IconButton } from 'design-system-web';
import { useChannelAdminContext } from 'hooks';
import { useDeleteVisitorWatchlist } from 'react-hooks';
import { FONT_AWESOME_REGULAR } from 'lane-shared/helpers/constants/icons';
import { convertToUUID } from 'uuid-encoding';

import styles from './DeleteWatchlistPrompt.scss';

type DeleteWatchlistPromptProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  watchlistId: string;
  watchlistName: string;
};

export function DeleteWatchlistPrompt({
  isOpen,
  onClose,
  onSuccess,
  watchlistId,
  watchlistName,
}: DeleteWatchlistPromptProps) {
  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  const { deleteWatchlist, loading } = useDeleteVisitorWatchlist();

  const handleDelete = async () => {
    if (!channel) {
      throw new Error('Channel is required');
    }

    await deleteWatchlist(watchlistId, convertToUUID(channel._id), () => {
      onSuccess();
      onClose();
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId="deleteWatchlistModal"
      className={styles.modal}
      includeHeader={false}
    >
      <Flex className={styles.modalHeader} justify="space-between" mb={5}>
        <H4 mt={2}>
          {t('web.admin.channel.visitor.watchlist.delete.modal.title')}
        </H4>
        <IconButton
          type={FONT_AWESOME_REGULAR}
          onClick={onClose}
          icon="times"
          data-test="closeButton"
          size="medium"
        />
      </Flex>
      <Flex direction="column" gap={5}>
        <Flex direction="column">
          <S variant="secondary">
            {t('web.admin.channel.visitor.watchlist.delete.modal.message', {
              name: watchlistName,
            })}
          </S>
          <S variant="secondary" mb={5}>
            {t('web.admin.channel.visitor.watchlist.delete.modal.warning')}
          </S>
        </Flex>
        <Flex gap={4}>
          <Button
            variant="primary"
            onClick={handleDelete}
            testId="delete-button"
            size="large"
            loading={loading}
          >
            {t('web.admin.channel.visitor.watchlist.delete.modal.confirm')}
          </Button>
          <Button
            variant="secondary"
            onClick={onClose}
            testId="cancel-button"
            size="large"
          >
            {t('web.admin.channel.visitor.watchlist.delete.modal.cancel')}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
